import { Paper } from "@mui/material";
import ReactECharts from "echarts-for-react";
import type { CallbackDataParams } from "echarts/types/dist/shared";
import { DelayForPareto } from "../models/DelayForPareto";

type Props = {
	delaysForPareto: DelayForPareto[];
	onBarClick?: (item: DelayForPareto) => void;
};

export const DelaysParetoGraphic = ({ delaysForPareto, onBarClick }: Props) => {
	const formatMinutes = (minutes: number) => {
		return new Intl.NumberFormat("en-US").format(minutes);
	};

	const getChartOptions = () => {
		const paretoData = delaysForPareto.map((item) => {
			const delayPath = item.DelayCodePath;
			let formattedDelayName = "";

			if (delayPath) {
				const parts = delayPath.split("/");
				// Filter out any empty strings that result from leading/trailing slashes
				const relevantParts = parts.filter((part) => part !== "");

				if (relevantParts.length >= 2) {
					// Get the second to last and the last part
					formattedDelayName = `${relevantParts[relevantParts.length - 2]}/${
						relevantParts[relevantParts.length - 1]
					}`;
				} else if (relevantParts.length === 1) {
					// If there's only one part (e.g., "/Delay1"), just use that
					formattedDelayName = relevantParts[0];
				}
			}

			return {
				...item,
				DelayCodeName: formattedDelayName || "",
				DelayCategoryName: item.DelayCategoryName || "",
				DelayCategoryColor: item.DelayCategoryColor || "#1890ff",
			};
		});

		const totalMinutes = paretoData.reduce(
			(sum, item) => sum + (item.TotalDelayMinutes || 0),
			0,
		);

		const cumulativePercentages: number[] = [];
		let runningSum = 0;
		for (const item of paretoData) {
			runningSum += item.TotalDelayMinutes || 0;
			cumulativePercentages.push(
				totalMinutes > 0
					? Math.round((runningSum / totalMinutes) * 1000) / 10
					: 0,
			);
		}

		const options = {
			tooltip: {
				trigger: "axis",
				axisPointer: {
					type: "shadow",
				},
				formatter: (params: CallbackDataParams[]) => {
					const barParam = params.find((p) => p.seriesName === "Delay Minutes");
					if (!barParam) return "";
					const currentItem = paretoData[barParam.dataIndex];
					const categoryInfo = currentItem.DelayCategoryName
						? `<br/>Category: ${currentItem.DelayCategoryName}`
						: "";
					return `${currentItem.DelayCodeName}<br/>Total Minutes: ${formatMinutes(barParam.value as number)} min${categoryInfo}`;
				},
			},
			grid: {
				left: "5%",
				right: "8%",
				bottom: "3%",
				containLabel: true,
			},
			xAxis: [
				{
					type: "category",
					data: paretoData.map((item) => item.DelayCodeName),
					axisTick: {
						alignWithLabel: true,
					},
					axisLabel: {
						rotate: 45,
						interval: 0,
					},
				},
			],
			yAxis: [
				{
					type: "value",
					name: "Minutes",
					nameLocation: "middle",
					nameGap: 50,
					max: totalMinutes,
					splitLine: {
						show: true,
					},
					axisLine: {
						show: true,
					},
					axisTick: {
						show: true,
					},
					axisLabel: {
						show: true,
						formatter: (value: number) => {
							return formatMinutes(value);
						},
					},
				},
				{
					type: "value",
					name: "%",
					nameLocation: "middle",
					nameGap: 30,
					min: 0,
					max: 100,
					splitLine: {
						show: false,
					},
					axisLine: {
						show: true,
					},
					axisTick: {
						show: true,
					},
					axisLabel: {
						show: true,
						formatter: (value: number) => `${value}%`,
					},
				},
			],
			series: [
				{
					name: "Delay Minutes",
					type: "bar",
					barWidth: "60%",
					data: paretoData.map((item) => item.TotalDelayMinutes || 0),
					itemStyle: {
						color: (params: CallbackDataParams) => {
							const item = paretoData[params.dataIndex];
							if (item.DelayCodeName === "Undeclared Delays") {
								return "#CE0000";
							}
							if (item.DelayCodeName === "MicroDelays") {
								return "#D100DA";
							}
							return item.DelayCategoryColor;
						},
					},
					label: {
						show: true,
						position: "top",
						formatter: (params: CallbackDataParams) => {
							return formatMinutes(params.value as number);
						},
					},
				},
				{
					name: "Cumulative %",
					type: "line",
					yAxisIndex: 1,
					data: cumulativePercentages,
					smooth: false,
					symbol: "circle",
					symbolSize: 6,
					lineStyle: {
						color: "#FF6600",
						width: 2,
					},
					itemStyle: {
						color: "#FF6600",
					},
					label: {
						show: false,
					},
				},
			],
		};

		return options;
	};

	const handleChartClick = (params: CallbackDataParams) => {
		if (
			params.componentType === "series" &&
			params.seriesName === "Delay Minutes" &&
			onBarClick
		) {
			const item = delaysForPareto[params.dataIndex];
			if (item) {
				onBarClick(item);
			}
		}
	};

	return (
		<div onContextMenu={(e) => e.preventDefault()}>
			<Paper
				sx={{
					height: "75vh",
					minHeight: "75vh",
					width: "100%",
				}}
				elevation={2}
			>
				<ReactECharts
					option={getChartOptions()}
					style={{ height: "100%", width: "100%" }}
					opts={{ renderer: "svg" }}
					onEvents={{ click: handleChartClick }}
				/>
			</Paper>
		</div>
	);
};
